import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { format } from 'date-fns';
import { WASTE_CATEGORIES, getCategoryColor } from '../config/wasteCategories';
import { FaBell, FaExclamationTriangle, FaChartLine, FaClock, FaCheckCircle, FaUsers, FaMapMarkerAlt, FaBuilding, FaRecycle, FaTruck, FaExclamationCircle, FaTimes, FaPlus, FaTrash, FaCheck, FaPlay, FaFileInvoice, FaCalculator, FaBoxes, FaUsersCog, FaQuestionCircle, FaBalanceScale, FaDownload } from 'react-icons/fa';
import './CorporateDashboard.css';
import ReportBuilder from '../components/ReportBuilder';
import DashboardMap from '../components/DashboardMap';
import { getCouncilCoordinates, UK_GEOGRAPHY } from '../data/ukGeography';

const PRIORITY_COLORS = {
  critical: '#dc2626',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e'
};

const PRIORITY_LABELS = {
  critical: { icon: '🔴', text: 'Critical', deadline: 'Immediate' },
  high: { icon: '🟠', text: 'High', deadline: '<24 hrs' },
  medium: { icon: '🟡', text: 'Medium', deadline: '<72 hrs' },
  low: { icon: '🟢', text: 'Low', deadline: '<7 days' }
};

const SUBSCRIBER_TYPES = {
  authority: { name: 'Local/Public Authority', icon: FaBuilding },
  private_company: { name: 'Private Waste Company', icon: FaTruck },
  recycling: { name: 'Waste2Wealth Recycling', icon: FaRecycle }
};

const getAllLocalAuthorities = () => {
  const authorities = [];
  Object.values(UK_GEOGRAPHY).forEach(country => {
    Object.values(country.regions).forEach(region => {
      authorities.push(...region.councils);
    });
  });
  return authorities.sort();
};

const CorporateDashboard = () => {
  const { loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState({ notifications: [], unreadCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(6);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState('all');
  
  // Operational Features State
  const [crews, setCrews] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [recyclableReports, setRecyclableReports] = useState([]);
  const [materialRates, setMaterialRates] = useState([]);
  const [inventory, setInventory] = useState({ items: [], summary: [] });
  const [operationalLoading, setOperationalLoading] = useState(false);
  
  // UI State
  const [showCrewModal, setShowCrewModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [newCrewName, setNewCrewName] = useState('');
  const [newCrewVehicle, setNewCrewVehicle] = useState('');
  const [selectedCrew, setSelectedCrew] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [valueEstimate, setValueEstimate] = useState(null);
  const [estimateMaterials, setEstimateMaterials] = useState([{ materialType: '', quantity: '' }]);
  const [severityHelpModal, setSeverityHelpModal] = useState(null); // 'wasteType', 'location', 'duration'
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  
  // Local Authority filter for Waste Management & Recycling companies
  const [selectedLocalAuthority, setSelectedLocalAuthority] = useState(null);
  const [selectedAuthorityBoundary, setSelectedAuthorityBoundary] = useState(null);
  const [selectedAuthorityCenter, setSelectedAuthorityCenter] = useState(null);
  const [selectedAuthorityReports, setSelectedAuthorityReports] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsData, notifData] = await Promise.all([
        api.getCorporateDashboardStats(timeRange),
        api.getNotifications()
      ]);
      setStats(statsData);
      setNotifications(notifData);
    } catch (err) {
      console.error('Error fetching corporate dashboard:', err);
      if (err.message === 'Subscriber access required') {
        setError('This dashboard is only available to corporate subscribers.');
      } else {
        setError('Failed to load dashboard data.');
      }
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  const fetchOperationalData = useCallback(async (type) => {
    setOperationalLoading(true);
    try {
      if (type === 'authority') {
        const [crewsData, assignmentsData] = await Promise.all([
          api.getCrews(),
          api.getAssignments()
        ]);
        setCrews(crewsData);
        setAssignments(assignmentsData);
      } else if (type === 'private_company') {
        const [jobsData, invoicesData, perfData] = await Promise.all([
          api.getJobs(),
          api.getInvoices(),
          api.getPerformanceMetrics()
        ]);
        setJobs(jobsData);
        setInvoices(invoicesData);
        setPerformance(perfData);
      } else if (type === 'recycling') {
        const [reportsData, ratesData, invData] = await Promise.all([
          api.getRecyclableReports(),
          api.getMaterialRates(),
          api.getInventory()
        ]);
        setRecyclableReports(reportsData);
        setMaterialRates(ratesData);
        setInventory(invData);
      }
    } catch (err) {
      console.error('Error fetching operational data:', err);
    } finally {
      setOperationalLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    fetchData();
  }, [authLoading, fetchData]);

  useEffect(() => {
    if (stats?.subscriberType) {
      fetchOperationalData(stats.subscriberType);
    }
  }, [stats?.subscriberType, fetchOperationalData]);

  // SSE connection for real-time notifications
  useEffect(() => {
    if (authLoading || !stats) return;
    
    const token = localStorage.getItem('authToken');
    if (!token) return;

    let reconnectTimeout = null;
    let abortController = null;

    const connectSSE = () => {
      const sseUrl = `${process.env.REACT_APP_API_URL || ''}/api/corporate-dashboard/notifications/stream`;
      
      abortController = new AbortController();
      
      fetch(sseUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream'
        },
        signal: abortController.signal
      }).then(response => {
        if (!response.ok) throw new Error('SSE connection failed');
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        
        const processStream = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              
              const text = decoder.decode(value);
              const lines = text.split('\n');
              
              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(line.slice(6));
                    
                    if (data.type === 'new_waste_report') {
                      // Add new notification to the list
                      setNotifications(prev => ({
                        unreadCount: prev.unreadCount + 1,
                        notifications: [{
                          id: Date.now(),
                          type: 'new_report',
                          message: `New ${data.data.category} waste reported: ${data.data.wasteType}`,
                          reportId: data.data.id,
                          timestamp: data.timestamp,
                          read: false
                        }, ...prev.notifications]
                      }));
                      
                      // Refresh dashboard data to include the new report
                      fetchData();
                    }
                  } catch (e) {
                    // Ignore parse errors for heartbeats etc
                  }
                }
              }
            }
          } catch (err) {
            if (err.name !== 'AbortError') {
              console.log('SSE stream ended, reconnecting...');
              reconnectTimeout = setTimeout(connectSSE, 5000);
            }
          }
        };
        
        processStream();
      }).catch(err => {
        if (err.name !== 'AbortError') {
          console.error('SSE connection error:', err);
          reconnectTimeout = setTimeout(connectSSE, 5000);
        }
      });

      return abortController;
    };

    const controller = connectSSE();

    return () => {
      if (controller) controller.abort();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, [authLoading, stats, fetchData]);

  // Authority Handlers
  const handleCreateCrew = async () => {
    if (!newCrewName.trim()) return;
    try {
      const crew = await api.createCrew(newCrewName, [], newCrewVehicle);
      setCrews(prev => [...prev, crew]);
      setNewCrewName('');
      setNewCrewVehicle('');
      setShowCrewModal(false);
    } catch (err) {
      console.error('Failed to create crew:', err);
    }
  };

  const handleDeleteCrew = async (crewId) => {
    try {
      await api.deleteCrew(crewId);
      setCrews(prev => prev.filter(c => c.id !== crewId));
    } catch (err) {
      console.error('Failed to delete crew:', err);
    }
  };

  const handleAssignToCrew = async () => {
    if (!selectedReport || !selectedCrew || !scheduledDate) return;
    try {
      await api.createAssignment(selectedReport.id, parseInt(selectedCrew), scheduledDate);
      setShowAssignModal(false);
      setSelectedReport(null);
      setSelectedCrew('');
      setScheduledDate('');
      fetchOperationalData('authority');
      fetchData();
    } catch (err) {
      console.error('Failed to create assignment:', err);
    }
  };

  const handleUpdateAssignmentStatus = async (assignmentId, newStatus) => {
    try {
      await api.updateAssignmentStatus(assignmentId, newStatus);
      setAssignments(prev => prev.map(a => 
        a.id === assignmentId ? { ...a, status: newStatus } : a
      ));
      if (newStatus === 'completed') fetchData();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  // Private Company Handlers
  const handleRespondToJob = async (jobId, response) => {
    try {
      await api.respondToJob(jobId, response);
      setJobs(prev => prev.map(j => 
        j.id === jobId ? { ...j, response, status: response === 'accepted' ? 'accepted' : 'declined' } : j
      ));
    } catch (err) {
      console.error('Failed to respond to job:', err);
    }
  };

  const handleCompleteJob = async (jobId) => {
    try {
      const job = jobs.find(j => j.id === jobId);
      await api.completeJob(jobId, job?.estimated_cost || 0);
      fetchOperationalData('private_company');
      fetchData();
    } catch (err) {
      console.error('Failed to complete job:', err);
    }
  };

  // Recycling Handlers
  const handleEstimateValue = async () => {
    const validMaterials = estimateMaterials.filter(m => m.materialType && m.quantity);
    if (validMaterials.length === 0) return;
    try {
      const result = await api.estimateMaterialValue(validMaterials);
      setValueEstimate(result);
    } catch (err) {
      console.error('Failed to estimate value:', err);
    }
  };

  const handleAddMaterialRow = () => {
    setEstimateMaterials(prev => [...prev, { materialType: '', quantity: '' }]);
  };

  const handleUpdateMaterial = (index, field, value) => {
    setEstimateMaterials(prev => prev.map((m, i) => 
      i === index ? { ...m, [field]: value } : m
    ));
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markNotificationsRead();
      setNotifications(prev => ({
        ...prev,
        notifications: prev.notifications.map(n => ({ ...n, read: true })),
        unreadCount: 0
      }));
    } catch (err) {
      console.error('Failed to mark notifications read:', err);
    }
  };

  // Handle local authority selection for Waste Management & Recycling companies
  const handleLocalAuthorityChange = async (authorityName) => {
    setSelectedLocalAuthority(authorityName);
    if (!authorityName) {
      // Reset to nationwide view
      setSelectedAuthorityBoundary(null);
      setSelectedAuthorityCenter(null);
      setSelectedAuthorityReports(null);
    } else {
      // Fetch boundary and reports for selected authority
      try {
        const [boundaryData, reportsData] = await Promise.all([
          api.getAuthorityBoundary(authorityName),
          api.getAuthorityReports(authorityName)
        ]);
        if (boundaryData) {
          setSelectedAuthorityBoundary(boundaryData.boundary);
          setSelectedAuthorityCenter([boundaryData.center.lat, boundaryData.center.lng]);
        }
        setSelectedAuthorityReports(reportsData || []);
      } catch (err) {
        console.error('Failed to fetch authority data:', err);
        setSelectedAuthorityBoundary(null);
        setSelectedAuthorityCenter(null);
        setSelectedAuthorityReports(null);
      }
    }
  };

  const filteredPriorityQueue = stats?.priorityQueue?.filter(r => 
    selectedPriority === 'all' || r.priority === selectedPriority
  ) || [];

  const subscriberType = stats?.subscriberType || 'authority';
  const SubscriberIcon = SUBSCRIBER_TYPES[subscriberType]?.icon || FaBuilding;

  const mapCenter = useMemo(() => {
    // For Waste Management/Recycling with selected authority, use that center
    if ((subscriberType === 'private_company' || subscriberType === 'recycling') && selectedAuthorityCenter) {
      return selectedAuthorityCenter;
    }
    // For authorities or default, use their territory
    const territory = stats?.territory || stats?.assignedAuthority;
    if (territory) {
      const coords = getCouncilCoordinates(territory);
      if (coords) return [coords.lat, coords.lng];
    }
    // Default to UK center for nationwide view
    return [54.5, -2.0];
  }, [stats, subscriberType, selectedAuthorityCenter]);

  // Determine which boundary to show
  const displayBoundary = useMemo(() => {
    // For Waste Management/Recycling with selected authority, show that boundary
    if ((subscriberType === 'private_company' || subscriberType === 'recycling') && selectedAuthorityBoundary) {
      return selectedAuthorityBoundary;
    }
    // For authorities, show their territory boundary
    return stats?.boundary;
  }, [stats, subscriberType, selectedAuthorityBoundary]);

  // Determine map zoom level
  const mapZoom = useMemo(() => {
    if (subscriberType === 'authority') return 12;
    if (selectedLocalAuthority) return 11;
    return 6; // Nationwide view
  }, [subscriberType, selectedLocalAuthority]);

  // Use authority-specific reports when an authority is selected, otherwise use all reports
  const allReports = useMemo(() => {
    if ((subscriberType === 'private_company' || subscriberType === 'recycling') && selectedAuthorityReports !== null) {
      return selectedAuthorityReports;
    }
    return stats?.priorityQueue || [];
  }, [stats, subscriberType, selectedAuthorityReports]);

  if (loading) {
    return (
      <div className="corporate-dashboard loading">
        <div className="loading-spinner">Loading Corporate Dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="corporate-dashboard error">
        <div className="error-message">
          <FaExclamationTriangle />
          <h2>Access Denied</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/individual-dashboard')}>Go to Individual Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="corporate-dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <SubscriberIcon className="subscriber-icon" />
          <div>
            <h1>Corporate Dashboard</h1>
            <p className="subscriber-type">
              {SUBSCRIBER_TYPES[subscriberType]?.name}
              {stats?.organizationName && <span>: <strong>{stats.organizationName}</strong></span>}
            </p>
          </div>
        </div>
        <div className="header-right">
          <button className="export-reports-btn" onClick={() => setShowReportBuilder(true)}>
            <FaDownload /> Export & Reports
          </button>
          <div className="time-select-wrapper">
            <label className="time-select-label">Select Dashboard Period</label>
            <select value={timeRange} onChange={(e) => setTimeRange(parseInt(e.target.value))}>
              <option value={1}>Last Month</option>
              <option value={3}>Last 3 Months</option>
              <option value={6}>Last 6 Months</option>
              <option value={12}>Last 12 Months</option>
              <option value={24}>Last 24 Months</option>
            </select>
          </div>
          <div className="notification-bell" onClick={() => setShowNotifications(!showNotifications)}>
            <FaBell />
            {notifications.unreadCount > 0 && (
              <span className="notification-badge">{notifications.unreadCount}</span>
            )}
          </div>
        </div>
      </header>

      {showNotifications && (
        <div className="notifications-dropdown">
          <div className="notifications-header">
            <h3>Alerts & Notifications</h3>
            <button onClick={handleMarkAllRead}>Mark all read</button>
            <button className="close-btn" onClick={() => setShowNotifications(false)}><FaTimes /></button>
          </div>
          <div className="notifications-list">
            {notifications.notifications.length === 0 ? (
              <p className="no-notifications">No notifications yet</p>
            ) : (
              notifications.notifications.slice(0, 10).map(notif => (
                <div key={notif.id} className={`notification-item ${notif.read ? 'read' : 'unread'} ${notif.priority}`}>
                  <span className="priority-dot" style={{ background: PRIORITY_COLORS[notif.priority] }}></span>
                  <div className="notification-content">
                    <strong>{notif.title}</strong>
                    <p>{notif.message}</p>
                    <span className="notification-time">{format(new Date(notif.createdAt), 'MMM dd, HH:mm')}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <div className="dashboard-grid">
        <section className="key-metrics">
          <div className="metric-card blue">
            <div className="metric-icon"><FaChartLine /></div>
            <div className="metric-value">{stats?.keyMetrics?.totalReportsThisMonth || 0}</div>
            <div className="metric-label">Reports This Month</div>
          </div>
          <div className="metric-card green">
            <div className="metric-icon"><FaCheckCircle /></div>
            <div className="metric-value">{stats?.keyMetrics?.cleanupRate || 0}%</div>
            <div className="metric-label">Cleanup Rate</div>
          </div>
          <div className="metric-card orange">
            <div className="metric-icon"><FaClock /></div>
            <div className="metric-value">{stats?.keyMetrics?.avgResponseTime || 0}h</div>
            <div className="metric-label">Avg Response Time</div>
          </div>
          <div className="metric-card red">
            <div className="metric-icon"><FaExclamationCircle /></div>
            <div className="metric-value">{stats?.keyMetrics?.pendingHighPriority || 0}</div>
            <div className="metric-label">High Priority Pending</div>
          </div>
        </section>

        <section className="priority-breakdown card">
          <h2><FaExclamationTriangle /> Priority Breakdown</h2>
          <div className="priority-bars">
            {['critical', 'high', 'medium', 'low'].map(priority => (
              <div key={priority} className="priority-bar-row">
                <span className="priority-label">
                  {PRIORITY_LABELS[priority].icon} {PRIORITY_LABELS[priority].text}
                </span>
                <div className="priority-bar-container">
                  <div 
                    className="priority-bar-fill"
                    style={{ 
                      width: `${Math.min(100, (stats?.priorityBreakdown?.[priority] || 0) / Math.max(1, stats?.keyMetrics?.pendingTotal || 1) * 100)}%`,
                      background: PRIORITY_COLORS[priority]
                    }}
                  ></div>
                </div>
                <span className="priority-count">{stats?.priorityBreakdown?.[priority] || 0}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="territory-map card full-width">
          <h2><FaMapMarkerAlt /> Territory Map</h2>
          <DashboardMap 
            reports={allReports}
            center={mapCenter}
            zoom={mapZoom}
            showFilters={true}
            showLocalAuthorityFilter={subscriberType === 'private_company' || subscriberType === 'recycling'}
            localAuthorities={getAllLocalAuthorities()}
            onLocalAuthorityChange={handleLocalAuthorityChange}
            title={selectedLocalAuthority || stats?.territory || stats?.assignedAuthority || 'Nationwide Coverage'}
            boundary={displayBoundary}
          />
        </section>

        <section className="severity-scoring card">
          <h2><FaBalanceScale /> Severity Waste Scoring System</h2>
          <p className="severity-desc">Enhanced flagging with automatic waste severity scoring formula</p>
          
          <div className="formula-display">
            <div className="formula-box">
              <span className="formula-text">Severity Score = </span>
              <span className="formula-component" onClick={() => setSeverityHelpModal('wasteType')}>
                Waste Type Weight <FaQuestionCircle className="help-icon" />
              </span>
              <span className="formula-operator">×</span>
              <span className="formula-component" onClick={() => setSeverityHelpModal('location')}>
                Location Sensitivity <FaQuestionCircle className="help-icon" />
              </span>
              <span className="formula-operator">×</span>
              <span className="formula-component" onClick={() => setSeverityHelpModal('duration')}>
                Duration Factor <FaQuestionCircle className="help-icon" />
              </span>
            </div>
          </div>

          <div className="severity-example">
            <div className="example-header">Example Calculation</div>
            <div className="example-content">
              <div className="example-row">
                <span>Electronic Waste (Weight: 7)</span>
                <span>×</span>
                <span>Residential (×1.3)</span>
                <span>×</span>
                <span>4 Days Old (×1.5)</span>
              </div>
              <div className="example-result">
                <span className="result-calculation">7 × 1.3 × 1.5 × 5 = </span>
                <span className="result-score high">68/100</span>
                <span className="severity-label high">High Severity</span>
              </div>
              <p className="formula-note">Score normalized to 0-100 scale (×5 factor), capped at 100</p>
            </div>
          </div>

          <div className="severity-thresholds">
            <div className="threshold-item critical">
              <span className="threshold-icon">🔴</span>
              <span className="threshold-range">70-100</span>
              <span className="threshold-label">Critical</span>
            </div>
            <div className="threshold-item high">
              <span className="threshold-icon">🟠</span>
              <span className="threshold-range">50-69</span>
              <span className="threshold-label">High</span>
            </div>
            <div className="threshold-item medium">
              <span className="threshold-icon">🟡</span>
              <span className="threshold-range">30-49</span>
              <span className="threshold-label">Medium</span>
            </div>
            <div className="threshold-item low">
              <span className="threshold-icon">🟢</span>
              <span className="threshold-range">0-29</span>
              <span className="threshold-label">Low</span>
            </div>
          </div>
        </section>

        <section className="priority-queue card">
          <div className="section-header">
            <h2><FaMapMarkerAlt /> Priority Waste Queue</h2>
            <select value={selectedPriority} onChange={(e) => setSelectedPriority(e.target.value)}>
              <option value="all">All Priorities</option>
              <option value="critical">Critical Only</option>
              <option value="high">High Only</option>
              <option value="medium">Medium Only</option>
              <option value="low">Low Only</option>
            </select>
          </div>
          <div className="queue-list">
            {filteredPriorityQueue.length === 0 ? (
              <p className="no-data">No pending reports</p>
            ) : (
              filteredPriorityQueue.slice(0, 10).map(report => (
                <div 
                  key={report.id} 
                  className={`queue-item priority-${report.priority}`}
                  style={{ borderLeftColor: PRIORITY_COLORS[report.priority] }}
                >
                  <div className="queue-priority">
                    <span className="priority-badge" style={{ background: PRIORITY_COLORS[report.priority] }}>
                      {PRIORITY_LABELS[report.priority].icon} {report.score}/100
                    </span>
                  </div>
                  <div className="queue-details">
                    <strong>{report.wasteType}</strong>
                    <span className="queue-category">{WASTE_CATEGORIES[report.category]?.name}</span>
                    <span className="queue-time">{format(new Date(report.timestamp), 'MMM dd, yyyy HH:mm')}</span>
                  </div>
                  <div className="queue-deadline">
                    <FaClock />
                    {PRIORITY_LABELS[report.priority].deadline}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="time-by-category card">
          <h2><FaClock /> Time-to-Cleanup by Category</h2>
          {stats?.timeByCategory?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stats.timeByCategory}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" tick={{ fontSize: 11 }} />
                <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value) => [`${value} hrs`, 'Avg Time']} />
                <Bar dataKey="avgHours" fill="#10b981" radius={[4, 4, 0, 0]}>
                  {stats.timeByCategory.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getCategoryColor(entry.category)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="no-data">No cleanup data available</p>
          )}
        </section>

        <section className="sla-compliance card">
          <h2><FaCheckCircle /> Service Level Agreement Compliance</h2>
          <div className="sla-metrics">
            <div className="sla-item">
              <div className="sla-circle" style={{ '--percentage': stats?.slaCompliance?.within24h || 0 }}>
                <span>{stats?.slaCompliance?.within24h || 0}%</span>
              </div>
              <span className="sla-label">Within 24h</span>
            </div>
            <div className="sla-item">
              <div className="sla-circle" style={{ '--percentage': stats?.slaCompliance?.within72h || 0 }}>
                <span>{stats?.slaCompliance?.within72h || 0}%</span>
              </div>
              <span className="sla-label">Within 72h</span>
            </div>
            <div className="sla-item">
              <div className="sla-circle" style={{ '--percentage': stats?.slaCompliance?.within7d || 0 }}>
                <span>{stats?.slaCompliance?.within7d || 0}%</span>
              </div>
              <span className="sla-label">Within 7 days</span>
            </div>
          </div>
        </section>

        <section className="response-trends card">
          <h2><FaChartLine /> Response Time Trends</h2>
          {stats?.responseTrends?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={stats.responseTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tickFormatter={(v) => format(new Date(v), 'MMM')} />
                <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
                <Tooltip labelFormatter={(v) => format(new Date(v), 'MMMM yyyy')} />
                <Line type="monotone" dataKey="avgHours" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6' }} name="Avg Response (hrs)" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="no-data">No trend data available</p>
          )}
        </section>

        <section className="contributors card">
          <h2><FaUsers /> Top Contributors</h2>
          <div className="contributors-list">
            {stats?.contributors?.length === 0 ? (
              <p className="no-data">No contributors yet</p>
            ) : (
              stats?.contributors?.map((c, i) => (
                <div key={i} className="contributor-row">
                  <span className="contributor-rank">#{c.rank}</span>
                  <span className="contributor-name">{c.name}</span>
                  <span className="contributor-reports">{c.reports} reports</span>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="weekly-trend card">
          <h2><FaChartLine /> Weekly Report Trend</h2>
          {stats?.weeklyTrend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={stats.weeklyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" tickFormatter={(v) => format(new Date(v), 'MMM dd')} />
                <YAxis />
                <Tooltip labelFormatter={(v) => `Week of ${format(new Date(v), 'MMM dd')}`} />
                <Bar dataKey="reports" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Reports" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="no-data">No weekly data available</p>
          )}
        </section>

        {/* ================== SUBSCRIBER-SPECIFIC OPERATIONAL SECTIONS ================== */}

        {/* AUTHORITY FEATURES */}
        {subscriberType === 'authority' && (
          <>
            <section className="crew-management card full-width">
              <div className="section-header">
                <h2><FaUsersCog /> Crew Management</h2>
                <button className="btn-primary" onClick={() => setShowCrewModal(true)}>
                  <FaPlus /> Add Crew
                </button>
              </div>
              {operationalLoading ? (
                <p className="loading-text">Loading crews...</p>
              ) : crews.length === 0 ? (
                <p className="no-data">No crews created yet. Add your first cleanup crew to start assigning waste reports.</p>
              ) : (
                <div className="crew-list">
                  {crews.map(crew => (
                    <div key={crew.id} className="crew-card">
                      <div className="crew-info">
                        <strong>{crew.name}</strong>
                        {crew.vehicle_info && <span className="crew-vehicle">{crew.vehicle_info}</span>}
                        <span className="crew-status">{crew.status}</span>
                      </div>
                      <button className="btn-danger-small" onClick={() => handleDeleteCrew(crew.id)}>
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="status-pipeline card full-width">
              <h2><FaClock /> Assignment Status Pipeline</h2>
              <div className="pipeline-container">
                {['scheduled', 'in_progress', 'completed'].map(status => (
                  <div key={status} className={`pipeline-column ${status}`}>
                    <div className="pipeline-header">
                      <h3>{status === 'in_progress' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1)}</h3>
                      <span className="count">{assignments.filter(a => a.status === status).length}</span>
                    </div>
                    <div className="pipeline-items">
                      {assignments.filter(a => a.status === status).map(assignment => (
                        <div key={assignment.id} className="pipeline-item">
                          <strong>{assignment.waste_type}</strong>
                          <span className="crew-name">{assignment.crew_name || 'Unassigned'}</span>
                          {assignment.scheduled_date && (
                            <span className="scheduled-date">{format(new Date(assignment.scheduled_date), 'MMM dd')}</span>
                          )}
                          <div className="pipeline-actions">
                            {status === 'scheduled' && (
                              <button onClick={() => handleUpdateAssignmentStatus(assignment.id, 'in_progress')}>
                                <FaPlay /> Start
                              </button>
                            )}
                            {status === 'in_progress' && (
                              <button onClick={() => handleUpdateAssignmentStatus(assignment.id, 'completed')}>
                                <FaCheck /> Complete
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="assign-waste card full-width">
              <h2><FaMapMarkerAlt /> Assign Waste to Crew</h2>
              <p className="section-desc">Select pending waste reports from the Priority Queue above, then assign them to a crew.</p>
              <div className="assign-grid">
                {stats?.priorityQueue?.slice(0, 6).map(report => (
                  <div key={report.id} className="assign-card" onClick={() => { setSelectedReport(report); setShowAssignModal(true); }}>
                    <span className="priority-badge" style={{ background: PRIORITY_COLORS[report.priority] }}>
                      {PRIORITY_LABELS[report.priority].icon}
                    </span>
                    <strong>{report.wasteType}</strong>
                    <span>{report.category}</span>
                    <span className="assign-action">Click to Assign</span>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {/* PRIVATE COMPANY FEATURES */}
        {subscriberType === 'private_company' && (
          <>
            <section className="job-management card full-width">
              <h2><FaTruck /> Job Inbox</h2>
              {operationalLoading ? (
                <p className="loading-text">Loading jobs...</p>
              ) : jobs.filter(j => j.status === 'pending').length === 0 ? (
                <p className="no-data">No pending jobs. Jobs assigned by authorities will appear here.</p>
              ) : (
                <div className="jobs-list">
                  {jobs.filter(j => j.status === 'pending').map(job => (
                    <div key={job.id} className="job-card pending">
                      <div className="job-info">
                        <strong>{job.waste_type}</strong>
                        <span>{job.category}</span>
                        <span className="job-issuer">From: {job.issuer_name}</span>
                        {job.estimated_cost && <span className="job-cost">Est: ${job.estimated_cost}</span>}
                      </div>
                      <div className="job-actions">
                        <button className="btn-success" onClick={() => handleRespondToJob(job.id, 'accepted')}>
                          <FaCheck /> Accept
                        </button>
                        <button className="btn-danger" onClick={() => handleRespondToJob(job.id, 'declined')}>
                          <FaTimes /> Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="active-jobs card full-width">
              <h2><FaPlay /> Active Jobs</h2>
              {jobs.filter(j => ['accepted', 'scheduled'].includes(j.status)).length === 0 ? (
                <p className="no-data">No active jobs</p>
              ) : (
                <div className="jobs-list">
                  {jobs.filter(j => ['accepted', 'scheduled'].includes(j.status)).map(job => (
                    <div key={job.id} className="job-card active">
                      <div className="job-info">
                        <strong>{job.waste_type}</strong>
                        <span>{job.category}</span>
                        <span className="job-status">{job.status}</span>
                        {job.scheduled_date && (
                          <span className="job-scheduled">Scheduled: {format(new Date(job.scheduled_date), 'MMM dd')}</span>
                        )}
                      </div>
                      <div className="job-actions">
                        <button className="btn-success" onClick={() => handleCompleteJob(job.id)}>
                          <FaCheck /> Mark Complete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="invoices-section card">
              <h2><FaFileInvoice /> Invoices</h2>
              {invoices.length === 0 ? (
                <p className="no-data">No invoices generated yet</p>
              ) : (
                <div className="invoice-list">
                  {invoices.slice(0, 5).map(inv => (
                    <div key={inv.id} className="invoice-row">
                      <span className="invoice-number">{inv.invoice_number}</span>
                      <span className="invoice-amount">${inv.total}</span>
                      <span className={`invoice-status ${inv.status}`}>{inv.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="performance-section card">
              <h2><FaChartLine /> Performance Metrics</h2>
              {performance?.summary ? (
                <div className="perf-metrics">
                  <div className="perf-item">
                    <span className="perf-value">{performance.summary.completed_jobs || 0}</span>
                    <span className="perf-label">Jobs Completed</span>
                  </div>
                  <div className="perf-item">
                    <span className="perf-value">{performance.summary.active_jobs || 0}</span>
                    <span className="perf-label">Active Jobs</span>
                  </div>
                  <div className="perf-item">
                    <span className="perf-value">{Math.round(performance.summary.avg_completion_hours || 0)}h</span>
                    <span className="perf-label">Avg Completion</span>
                  </div>
                  <div className="perf-item">
                    <span className="perf-value">${parseFloat(performance.summary.total_revenue || 0).toFixed(0)}</span>
                    <span className="perf-label">Total Revenue</span>
                  </div>
                </div>
              ) : (
                <p className="no-data">No performance data available</p>
              )}
            </section>
          </>
        )}

        {/* RECYCLING FEATURES */}
        {subscriberType === 'recycling' && (
          <>
            <section className="recyclable-reports card full-width">
              <h2><FaRecycle /> Recyclable Waste Reports</h2>
              <p className="section-desc">Showing only Electronic, Construction, and Automobile waste (recyclable materials)</p>
              {operationalLoading ? (
                <p className="loading-text">Loading recyclable reports...</p>
              ) : recyclableReports.length === 0 ? (
                <p className="no-data">No pending recyclable waste reports</p>
              ) : (
                <div className="recyclable-grid">
                  {recyclableReports.slice(0, 8).map(report => (
                    <div key={report.id} className="recyclable-card">
                      <span className={`category-badge ${report.category?.toLowerCase()}`}>{report.category}</span>
                      <strong>{report.waste_type}</strong>
                      <span className="report-date">{format(new Date(report.timestamp), 'MMM dd, yyyy')}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="value-estimator card">
              <h2><FaCalculator /> Material Value Estimator</h2>
              <div className="estimator-form">
                {estimateMaterials.map((mat, idx) => (
                  <div key={idx} className="material-row">
                    <select 
                      value={mat.materialType} 
                      onChange={(e) => handleUpdateMaterial(idx, 'materialType', e.target.value)}
                    >
                      <option value="">Select Material</option>
                      {materialRates.map(r => (
                        <option key={r.id} value={r.material_type}>{r.material_type} (${r.rate_per_kg}/kg)</option>
                      ))}
                    </select>
                    <input 
                      type="number" 
                      placeholder="Quantity (kg)" 
                      value={mat.quantity}
                      onChange={(e) => handleUpdateMaterial(idx, 'quantity', e.target.value)}
                    />
                  </div>
                ))}
                <div className="estimator-actions">
                  <button className="btn-secondary" onClick={handleAddMaterialRow}>
                    <FaPlus /> Add Material
                  </button>
                  <button className="btn-primary" onClick={handleEstimateValue}>
                    <FaCalculator /> Calculate Value
                  </button>
                </div>
                {valueEstimate && (
                  <div className="estimate-result">
                    <h4>Estimated Total Value: <span className="value-amount">${valueEstimate.totalValue}</span></h4>
                    <div className="estimate-breakdown">
                      {valueEstimate.breakdown.map((item, i) => (
                        <div key={i} className="breakdown-row">
                          <span>{item.materialType}</span>
                          <span>{item.quantity} kg x ${item.rate}/kg = ${item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>

            <section className="inventory-section card">
              <h2><FaBoxes /> Material Inventory</h2>
              {inventory.summary.length === 0 ? (
                <p className="no-data">No materials in inventory yet</p>
              ) : (
                <div className="inventory-summary">
                  {inventory.summary.map((item, i) => (
                    <div key={i} className="inventory-item">
                      <span className="material-name">{item.material_type}</span>
                      <span className="material-qty">{parseFloat(item.total_quantity).toFixed(1)} kg</span>
                      <span className="material-value">${parseFloat(item.total_value || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {/* MODALS */}
      {showCrewModal && (
        <div className="modal-overlay" onClick={() => setShowCrewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Add New Crew</h3>
            <input 
              type="text" 
              placeholder="Crew Name (e.g., Team Alpha)" 
              value={newCrewName}
              onChange={(e) => setNewCrewName(e.target.value)}
            />
            <input 
              type="text" 
              placeholder="Vehicle Info (optional)" 
              value={newCrewVehicle}
              onChange={(e) => setNewCrewVehicle(e.target.value)}
            />
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCrewModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleCreateCrew}>Create Crew</button>
            </div>
          </div>
        </div>
      )}

      {showAssignModal && selectedReport && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Assign Waste to Crew</h3>
            <div className="assign-details">
              <p><strong>Waste Type:</strong> {selectedReport.wasteType}</p>
              <p><strong>Category:</strong> {selectedReport.category}</p>
              <p><strong>Priority:</strong> {PRIORITY_LABELS[selectedReport.priority].text}</p>
            </div>
            <select value={selectedCrew} onChange={(e) => setSelectedCrew(e.target.value)}>
              <option value="">Select Crew</option>
              {crews.map(crew => (
                <option key={crew.id} value={crew.id}>{crew.name}</option>
              ))}
            </select>
            <input 
              type="datetime-local" 
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
            />
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowAssignModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleAssignToCrew}>Assign</button>
            </div>
          </div>
        </div>
      )}

      {/* Severity Scoring Help Modals */}
      {severityHelpModal === 'wasteType' && (
        <div className="modal-overlay" onClick={() => setSeverityHelpModal(null)}>
          <div className="modal-content severity-help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Waste Type Weights</h3>
              <button className="close-btn" onClick={() => setSeverityHelpModal(null)}><FaTimes /></button>
            </div>
            <p className="help-desc">Different waste types have different severity weights based on their potential harm to health and environment:</p>
            <div className="weight-table">
              <div className="weight-row critical-row">
                <span className="weight-type">Hospital/Medical Waste</span>
                <span className="weight-value">10</span>
              </div>
              <div className="weight-row high-row">
                <span className="weight-type">Environmental/Municipal</span>
                <span className="weight-value">9</span>
              </div>
              <div className="weight-row high-row">
                <span className="weight-type">Electronic Waste (E-Waste)</span>
                <span className="weight-value">7</span>
              </div>
              <div className="weight-row medium-row">
                <span className="weight-type">Commercial/Industrial</span>
                <span className="weight-value">6</span>
              </div>
              <div className="weight-row medium-row">
                <span className="weight-type">Construction/Automobile</span>
                <span className="weight-value">5</span>
              </div>
              <div className="weight-row low-row">
                <span className="weight-type">Domestic/Household</span>
                <span className="weight-value">3</span>
              </div>
              <div className="weight-row low-row">
                <span className="weight-type">Biodegradable</span>
                <span className="weight-value">2</span>
              </div>
              <div className="weight-row low-row">
                <span className="weight-type">Other Types</span>
                <span className="weight-value">1-5</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {severityHelpModal === 'location' && (
        <div className="modal-overlay" onClick={() => setSeverityHelpModal(null)}>
          <div className="modal-content severity-help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Location Sensitivity Multipliers</h3>
              <button className="close-btn" onClick={() => setSeverityHelpModal(null)}><FaTimes /></button>
            </div>
            <p className="help-desc">Location sensitivity increases the severity score based on potential exposure to vulnerable populations:</p>
            <div className="weight-table">
              <div className="weight-row critical-row">
                <span className="weight-type">Near School/Hospital/Playground</span>
                <span className="weight-value">×1.5</span>
              </div>
              <div className="weight-row critical-row">
                <span className="weight-type">Near Water Source/River</span>
                <span className="weight-value">×1.5</span>
              </div>
              <div className="weight-row high-row">
                <span className="weight-type">Residential High-Density Area</span>
                <span className="weight-value">×1.3</span>
              </div>
              <div className="weight-row medium-row">
                <span className="weight-type">Residential Standard</span>
                <span className="weight-value">×1.2</span>
              </div>
              <div className="weight-row low-row">
                <span className="weight-type">Rural/Low-Density Area</span>
                <span className="weight-value">×1.0</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {severityHelpModal === 'duration' && (
        <div className="modal-overlay" onClick={() => setSeverityHelpModal(null)}>
          <div className="modal-content severity-help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Duration Factors</h3>
              <button className="close-btn" onClick={() => setSeverityHelpModal(null)}><FaTimes /></button>
            </div>
            <p className="help-desc">The longer waste remains unreported/uncleaned, the higher the severity multiplier:</p>
            <div className="weight-table">
              <div className="weight-row low-row">
                <span className="weight-type">Less than 24 hours</span>
                <span className="weight-value">×1.0</span>
              </div>
              <div className="weight-row medium-row">
                <span className="weight-type">1-3 days</span>
                <span className="weight-value">×1.2</span>
              </div>
              <div className="weight-row high-row">
                <span className="weight-type">3-7 days</span>
                <span className="weight-value">×1.5</span>
              </div>
              <div className="weight-row critical-row">
                <span className="weight-type">More than 7 days</span>
                <span className="weight-value">×2.0</span>
              </div>
            </div>
            <p className="help-note">Note: Severity increases exponentially as waste remains unaddressed, reflecting growing health and environmental risks.</p>
          </div>
        </div>
      )}

      <ReportBuilder 
        isOpen={showReportBuilder} 
        onClose={() => setShowReportBuilder(false)}
        subscriberType={subscriberType}
      />
    </div>
  );
};

export default CorporateDashboard;
